import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Centralised user/role state.
//
// PR2 of the admin-roles refactor introduced this so RequireAdmin and
// Header.js can read the current user's roles without each page refetching
// /auth/me independently. Existing per-page user fetches are NOT replaced —
// they keep working. The provider is additive.
//
// Shape: { user, loading, refresh, logout }
//   user     — the /auth/me response, or null if not logged in / fetch failed
//   loading  — true until the initial fetch resolves (so RequireAdmin can
//              render a spinner instead of redirecting prematurely on hard
//              refresh of an admin page)
//   refresh  — call after a profile change so the context picks up new state
//   logout   — clears user_token and user state; callers handle navigation

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setUser(data);
    } catch (err) {
      // Only treat 401/403 as deauth. Transient failures (timeouts, 5xx,
      // network errors, CORS, aborts) leave user state alone — otherwise
      // a Railway cold start on tab return would log valid sessions out
      // even though the JWT in localStorage is still good. The 401 path
      // itself is still handled by the global interceptor in api.js (which
      // clears the token and redirects).
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setUser(null);
      } else {
        console.warn("Transient /auth/me failure, keeping last-known user state:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
    // Re-sync if another tab logs in/out by mutating localStorage.user_token.
    const onStorage = (e) => {
      if (e.key === "user_token") fetchMe();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchMe]);

  const logout = useCallback(() => {
    localStorage.removeItem("user_token");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh: fetchMe, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

// Helpers consumers reach for repeatedly. Keep the membership rules in one
// place so RequireAdmin, Header, and ManageAdminsPage agree.
export function hasAdminRole(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.includes("admin") || roles.includes("super_admin");
}

export function hasSuperAdminRole(user) {
  if (!user) return false;
  return (user.roles || []).includes("super_admin");
}
